import { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { ArrowUpRight } from 'lucide-react'
import BrandLockup from './BrandLockup.jsx'

const navLinks = [
  { label: 'About', path: '/about' },
  { label: 'Work', path: '/work' },
  { label: 'Services', path: '/services' },
  { label: 'Philosophy', path: '/philosophy' },
  { label: 'Contact', path: '/contact' },
]

export default function Header() {
  const [open, setOpen] = useState(false)
  const [visible, setVisible] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const timer = setTimeout(() => setVisible(true), reducedMotion ? 0 : 420)
    return () => clearTimeout(timer)
  }, [])

  const handleNavClick = (path) => {
    setOpen(false)
    if (path.startsWith('/#')) {
      const hash = path.replace('/#', '')
      if (location.pathname === '/') {
        const el = document.getElementById(hash)
        if (el) el.scrollIntoView({ behavior: 'smooth' })
      } else {
        navigate('/#' + hash)
      }
    }
  }

  return (
    <header
      className={`fixed top-0 left-0 right-0 h-20 z-[70] flex items-center justify-between transition-opacity duration-800 ${visible ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'}`}
      style={{ padding: '0 clamp(0.75rem, 4vw, 2rem)' }}
    >
      <div className="hidden md:flex items-center justify-between w-full h-full gap-2">
        <div className="flex items-center gap-2">
          <Link
            to="/"
            className="h-12 px-5 flex items-center gap-2.5 rounded-[14px] border border-white/10 bg-[#020b1f]/80 backdrop-blur-md hover:bg-[#041536]/90 active:scale-95 transition-all"
          >
            <BrandLockup
              className="gap-2.5"
              markClassName="h-7 w-7"
              wordmarkClassName="whitespace-nowrap font-sans text-base font-medium tracking-tight text-white"
            />
          </Link>

          <div
            className={`h-12 flex items-center overflow-hidden rounded-[14px] border border-white/10 bg-[#020b1f]/80 backdrop-blur-md transition-all duration-350 ${open ? 'w-[420px]' : 'w-12'}`}
            style={{ transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)' }}
          >
            <button
              onClick={() => setOpen(!open)}
              className="w-12 h-12 flex items-center justify-center shrink-0 relative"
              aria-label="Toggle Menu"
            >
              <div className="w-[18px] h-3 relative">
                <span
                  className="absolute left-0 w-[18px] h-[1.5px] bg-white rounded-sm transition-transform duration-300"
                  style={{
                    top: 0,
                    transform: open ? 'translateY(6px) rotate(45deg)' : 'none',
                    transformOrigin: 'center',
                  }}
                />
                <span
                  className="absolute left-0 w-[18px] h-[1.5px] bg-white rounded-sm transition-all duration-300"
                  style={{
                    top: '6px',
                    opacity: open ? 0 : 1,
                    transform: open ? 'scale(0)' : 'none',
                  }}
                />
                <span
                  className="absolute left-0 w-[18px] h-[1.5px] bg-white rounded-sm transition-transform duration-300"
                  style={{
                    top: '12px',
                    transform: open ? 'translateY(-6px) rotate(-45deg)' : 'none',
                    transformOrigin: 'center',
                  }}
                />
              </div>
            </button>

            <nav
              className={`flex items-center gap-6 ml-auto pr-6 whitespace-nowrap transition-all duration-150 ${open ? 'opacity-100 translate-x-0 pointer-events-auto' : 'opacity-0 translate-x-4 pointer-events-none'}`}
            >
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => handleNavClick(link.path)}
                  className="text-base font-normal text-white/85 hover:text-white cursor-pointer transition-colors font-sans"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>
        </div>

        <Link
          to="/contact"
          className="group h-12 px-6 bg-white text-black rounded-full flex items-center gap-2.5 hover:bg-brand-teal hover:scale-103 active:scale-97 transition-all"
        >
          <span className="text-base font-medium text-black whitespace-nowrap font-sans">Bring the requirement</span>
          <ArrowUpRight className="w-4 h-4 text-black group-hover:rotate-45 transition-transform" />
        </Link>
      </div>

      <div className="flex md:hidden items-center justify-between w-full h-full gap-2 min-w-0">
        <div className="flex items-center h-9 min-w-0 flex-1 mr-2">
          <Link
            to="/"
            className={`h-9 px-2.5 flex items-center gap-1.5 overflow-hidden whitespace-nowrap rounded-[10px] border border-white/10 bg-[#020b1f]/80 backdrop-blur-md transition-all duration-350 shrink-0 ${open ? 'w-0 opacity-0 mr-0 px-0 pointer-events-none' : 'w-auto opacity-100 mr-1.5'}`}
            style={{ borderRadius: '10px' }}
          >
            <BrandLockup
              className="gap-1.5"
              markClassName="h-5 w-5"
              wordmarkClassName="whitespace-nowrap font-sans text-sm font-medium tracking-tight text-white"
            />
          </Link>

          <div
            className={`h-9 flex items-center overflow-hidden rounded-[10px] border border-white/10 bg-[#020b1f]/80 backdrop-blur-md transition-all duration-350 min-w-0 ${open ? 'flex-1' : 'w-9'}`}
            style={{ borderRadius: '10px', transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)' }}
          >
            <button
              onClick={() => setOpen(!open)}
              className="w-9 h-9 flex items-center justify-center shrink-0"
              aria-label="Toggle Menu Mobile"
            >
              <div className="w-[15px] h-2.5 relative">
                <span
                  className="absolute left-0 w-[15px] h-[1.2px] bg-white rounded-sm transition-transform duration-300"
                  style={{
                    top: 0,
                    transform: open ? 'translateY(5px) rotate(45deg)' : 'none',
                    transformOrigin: 'center',
                  }}
                />
                <span
                  className="absolute left-0 w-[15px] h-[1.2px] bg-white rounded-sm transition-all duration-300"
                  style={{
                    top: '5px',
                    opacity: open ? 0 : 1,
                    transform: open ? 'scale(0)' : 'none',
                  }}
                />
                <span
                  className="absolute left-0 w-[15px] h-[1.2px] bg-white rounded-sm transition-transform duration-300"
                  style={{
                    top: '10px',
                    transform: open ? 'translateY(-5px) rotate(-45deg)' : 'none',
                    transformOrigin: 'center',
                  }}
                />
              </div>
            </button>

            <nav
              className={`flex items-center gap-3.5 ml-auto pr-3.5 whitespace-nowrap transition-all duration-150 ${open ? 'opacity-100 translate-x-0 pointer-events-auto' : 'opacity-0 translate-x-2.5 pointer-events-none'}`}
            >
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => handleNavClick(link.path)}
                  className="text-sm font-normal text-white/85 hover:text-white cursor-pointer transition-colors font-sans"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>
        </div>

        <Link
          to="/contact"
          className="group h-9 px-2.5 sm:px-3.5 bg-white text-black rounded-full flex items-center gap-1 hover:bg-brand-teal hover:scale-103 active:scale-97 transition-all shrink-0"
        >
          <span className="text-sm font-medium text-black whitespace-nowrap font-sans">Contact</span>
          <ArrowUpRight className="w-3.5 h-3.5 text-black group-hover:rotate-45 transition-transform" />
        </Link>
      </div>
    </header>
  )
}
